import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Stepper } from '../../../../components/Stepper';
import { Tooltip } from '../../../../components/Tooltip';
import { DateField } from '../../../../components/inputs/DateField';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { toDateTimeLocal } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { receivedDocumentsApi } from '../routingApi';
import { CORRESPONDENT_KIND_OPTIONS, EMPTY_NAME, nameIncomplete } from '../routingForm';
import type { CorrespondentKind, PersonNameParts, ReceivedDocumentDetail, RoutingUnitOption } from '../routingTypes';
import { useDocumentTypeField } from '../useDocumentTypeField';
import { sampleReceivedDocument } from '../utils/sampleRouting';
import { CorrespondentNameFields } from './CorrespondentNameFields';

const ID_PREFIX = 'received-document';

/*
  THREE STEPS — product call, 10 Oct 2026: Origin (From to the named sender) · Document Detail
  (Subject to the date on the document) · Receiving (Received to Remarks). The tick and Submit
  sit on the last; a refusal goes to the step owning its field.
*/
type Step = 'origin' | 'detail' | 'receiving';

const STEPS: Step[] = ['origin', 'detail', 'receiving'];

const STEP_LABEL: Record<Step, string> = {
  origin: 'Origin',
  detail: 'Document Detail',
  receiving: 'Receiving',
};

/** Which step owns each field the server can name — anything unlisted is the sender's. */
const FIELD_STEP: Record<string, Step> = {
  subject: 'detail',
  documentTypeId: 'detail',
  documentDate: 'detail',
  receivedAt: 'receiving',
  receivingOrgUnitId: 'receiving',
  remarks: 'receiving',
};

/**
 * Encode or correct a received HARD COPY.
 *
 * **Paper only, and so no files.** A soft copy between units travels as a dispatch and lands in
 * the recipient's Inbox; this register is the receiving desk's log of what physically arrived.
 *
 * **The sender is a unit OR an outside organization** — one radio, then one field. A unit is
 * picked from the org chart, so its name is never retyped and never drifts; an outside
 * organization is typed. **Only the outside one carries a named person**, written to the identity
 * store and drawn directly beneath the organization it signed for.
 *
 * **THREE STEPS** — where it came from, what it is, and where and when it arrived. A unit does not
 * receive from itself; that clash is the RECEIVING step's to answer, since it is the later choice.
 *
 * **The receiving unit is FIXED once the document has moved** — the server refuses a change, and
 * the field is disabled for that STATE with the reason in its description.
 */
export function ReceivedDocumentModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: ReceivedDocumentDetail | null;
  onClose: () => void;
  onSaved: (saved: ReceivedDocumentDetail) => void;
}) {
  const toast = useToast();

  // Seeded once, on mount — the parent mounts this only while it is open.
  const [subject, setSubject] = useState(editing?.subject ?? '');

  // Most paper arrives from outside, so a create opens there; an edit says what the record says.
  const [senderKind, setSenderKind] = useState<CorrespondentKind>(
    editing?.senderOrgUnitId != null ? 'internal' : 'external',
  );
  const [senderUnitId, setSenderUnitId] = useState(editing?.senderOrgUnitId ?? '');
  const [senderOffice, setSenderOffice] = useState(editing?.senderOffice ?? '');
  const [sender, setSender] = useState<PersonNameParts>(EMPTY_NAME);
  const [senderReference, setSenderReference] = useState(editing?.senderReferenceNumber ?? '');
  const [documentDate, setDocumentDate] = useState(editing?.documentDate ?? '');

  // `datetime-local` in the reader's own zone; converted to an instant on save. Now, on a create.
  const [receivedAt, setReceivedAt] = useState(toDateTimeLocal(editing ? editing.receivedAt : new Date()));
  const [unitId, setUnitId] = useState(editing?.receivingOrgUnitId ?? '');
  const [remarks, setRemarks] = useState(editing?.remarks ?? '');

  const type = useDocumentTypeField(editing ? { id: editing.documentTypeId, name: editing.documentTypeName } : null);

  const [units, setUnits] = useState<RoutingUnitOption[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(true);

  const [stepIndex, setStepIndex] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    receivedDocumentsApi
      .units(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setUnits(rows);
      })
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) setLoadingUnits(false);
      });

    return () => controller.abort();
  }, []);

  const liveUnits = useMemo(
    () => units.map((u) => ({ value: u.id, label: u.name, sublabel: u.code ?? undefined, keywords: u.code ?? undefined })),
    [units],
  );

  // A unit already recorded, even if deleted since — the field states what the record says.
  const withRecorded = (options: typeof liveUnits, id: string | null | undefined, name: string | null | undefined) =>
    id != null && name != null && !units.some((u) => u.id === id) ? [{ value: id, label: name }, ...options] : options;

  const internal = senderKind === 'internal';

  // A unit does not receive from itself, and the server says so too — neither list offers the other's pick.
  const receivingOptions = withRecorded(
    liveUnits.filter((o) => !internal || o.value !== senderUnitId),
    editing?.receivingOrgUnitId,
    editing?.receivingOrgUnitName,
  );

  const senderOptions = withRecorded(
    liveUnits.filter((o) => o.value !== unitId),
    editing?.senderOrgUnitId,
    editing?.senderOrgUnitName,
  );

  const unitLocked = editing !== null && !editing.canChangeReceivingUnit;

  const missingOn: Record<Step, string | null> = {
    origin: internal
      ? senderUnitId === '' ? 'Choose the unit it came from.' : null
      : senderOffice.trim().length === 0
        ? 'Name the organization it came from.'
        : nameIncomplete(sender)
          ? 'Give both the first and last name of the named sender, or clear it.'
          : null,
    detail: subject.trim().length === 0 ? 'Give the subject.' : !type.answered ? 'Choose the document type.' : null,
    receiving:
      receivedAt === ''
        ? 'Give when it was received.'
        : unitId === ''
          ? 'Choose the receiving unit.'
          : internal && unitId === senderUnitId
            ? 'The receiving unit is the unit that sent it — a unit does not receive from itself.'
            : null,
  };

  const currentStep = STEPS[Math.min(stepIndex, STEPS.length - 1)];
  const isLastStep = currentStep === STEPS[STEPS.length - 1];
  const missing = STEPS.map((step) => missingOn[step]).find((m) => m !== null) ?? null;
  const canSave = missing === null && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const documentTypeId = await type.resolve();

      // ONE side of the sender reaches the wire; the other is cleared, never sent stale.
      const body = {
        subject: subject.trim(),
        documentTypeId,
        senderOrgUnitId: internal ? senderUnitId : null,
        senderOffice: internal ? null : senderOffice.trim(),
        senderSalutation: internal ? null : sender.salutation.trim() || null,
        senderFirstName: internal ? null : sender.firstName.trim() || null,
        senderMiddleName: internal ? null : sender.middleName.trim() || null,
        senderLastName: internal ? null : sender.lastName.trim() || null,
        senderSuffix: internal ? null : sender.suffix.trim() || null,
        senderReferenceNumber: senderReference.trim() || null,
        documentDate: documentDate || null,
        receivedAt: new Date(receivedAt).toISOString(),
        receivingOrgUnitId: unitId,
        remarks: remarks.trim() || null,
      };

      const saved = editing
        ? await receivedDocumentsApi.update(editing.id, body)
        : await receivedDocumentsApi.create(body);

      toast.success(editing ? `Saved ${saved.trackingNumber}.` : `Received ${saved.trackingNumber}.`);
      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this document.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: ID_PREFIX,
        stepOf: (field) => STEPS.indexOf(FIELD_STEP[field] ?? 'origin'),
        onStep: setStepIndex,
      });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleReceivedDocument({ typeIds: type.liveIds, unitIds: units.map((u) => u.id) });

    setSubject(sample.subject);
    type.set(sample.documentTypeId);
    setSenderUnitId(sample.senderOrgUnitId);
    setSenderOffice(sample.senderOffice);
    setSender(sample.sender);
    setSenderReference(sample.senderReferenceNumber);
    setDocumentDate(sample.documentDate);
    setReceivedAt(sample.receivedAt);
    if (!unitLocked) setUnitId(sample.receivingOrgUnitId);
    setRemarks(sample.remarks);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.trackingNumber}` : 'Receive document'}
      size="2xl"
      id={`${ID_PREFIX}-modal`}
      // Not until the catalogs it resolves foreign keys against have answered — it would fill none.
      autogenerate={{ onGenerate: generate, disabled: saving || type.loading || loadingUnits }}
      // The tick appears on the last step — the strip arriving IS the signal nothing is left to fill.
      confirm={isLastStep ? { id: `${ID_PREFIX}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving } : undefined}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {stepIndex > 0 ? (
            <Button
              id={`${ID_PREFIX}-back`}
              variant="secondary"
              onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            <Tooltip disabledReason={missingOn[currentStep]}>
              <Button
                id={`${ID_PREFIX}-next`}
                variant="secondary"
                onClick={() => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1))}
                disabled={missingOn[currentStep] !== null}
              >
                Next
              </Button>
            </Tooltip>
          ) : null}

          <Tooltip disabledReason={missing}>
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Stepper
          id={ID_PREFIX}
          steps={STEPS.map((key) => ({ key, label: STEP_LABEL[key] }))}
          activeIndex={stepIndex}
          onJump={(index) => {
            if (index <= stepIndex || STEPS.slice(0, index).every((step) => missingOn[step] === null)) setStepIndex(index);
          }}
        />

        <div
          id={`${ID_PREFIX}-panel-${currentStep}`}
          role="tabpanel"
          aria-labelledby={`${ID_PREFIX}-step-${currentStep}`}
          className="flex flex-col gap-4"
        >
          {currentStep === 'origin' ? (
            <>
              <RadioGroup<CorrespondentKind>
                id={`${ID_PREFIX}-sender-kind`}
                label="From"
                required
                value={senderKind}
                onChange={setSenderKind}
                options={CORRESPONDENT_KIND_OPTIONS}
                columns={2}
                disabled={saving}
              />

              {/* The sender and the number THEY printed on it — one row, whichever side the sender is on. */}
              <div className="grid gap-4 sm:grid-cols-2">
                {internal ? (
                  <Select
                    id={`${ID_PREFIX}-sender-org-unit-id`}
                    label="From (unit)"
                    required
                    description="The unit of this organization that sent it. The receiving unit is not offered."
                    value={senderUnitId}
                    onChange={setSenderUnitId}
                    options={senderOptions}
                    remote
                    loading={loadingUnits}
                    disabled={saving}
                    error={error?.fieldError('senderOrgUnitId')}
                  />
                ) : (
                  <TextField
                    id={`${ID_PREFIX}-sender-office`}
                    label="From (organization)"
                    required
                    description="The office, agency or company it came from — an organization, not a person. The person who signed it, if any, goes in the named sender below."
                    value={senderOffice}
                    onChange={(e) => setSenderOffice(e.target.value)}
                    maxLength={200}
                    disabled={saving}
                    error={error?.fieldError('senderOffice')}
                  />
                )}

                <TextField
                  id={`${ID_PREFIX}-sender-reference-number`}
                  label="Sender's reference no."
                  description="The control or memo number printed on the document by whoever sent it. Optional."
                  value={senderReference}
                  onChange={(e) => setSenderReference(e.target.value)}
                  maxLength={100}
                  className="font-mono"
                  disabled={saving}
                  error={error?.fieldError('senderReferenceNumber')}
                />
              </div>

              {/* Directly beneath the organization it signed for — an outside sender only. */}
              {internal ? null : (
                <CorrespondentNameFields
                  idPrefix={ID_PREFIX}
                  role="sender"
                  field="sender"
                  value={sender}
                  onChange={setSender}
                  editing={editing !== null && editing.senderPersonId != null}
                  error={error}
                  disabled={saving}
                />
              )}
            </>
          ) : currentStep === 'detail' ? (
            <>
              <TextField
                id={`${ID_PREFIX}-subject`}
                label="Subject"
                required
                description="What the document is about, as its subject line reads."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={300}
                disabled={saving}
                error={error?.fieldError('subject')}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <Select
                  id={`${ID_PREFIX}-document-type-id`}
                  label="Document type"
                  required
                  description={
                    type.canCreate
                      ? 'What kind of document it is. Type a new kind to add it to the catalog when this is saved.'
                      : 'What kind of document it is, from the catalog under Routing › Settings.'
                  }
                  value={type.value}
                  onChange={type.onChange}
                  options={type.options}
                  remote
                  loading={type.loading}
                  allowCustom={type.canCreate}
                  onCreateOption={type.onCreateOption}
                  disabled={saving}
                  error={error?.fieldError('documentTypeId')}
                />

                <DateField
                  id={`${ID_PREFIX}-document-date`}
                  label="Date on the document"
                  description="The date printed on it. Optional."
                  value={documentDate}
                  onChange={setDocumentDate}
                  disabled={saving}
                  error={error?.fieldError('documentDate')}
                />
              </div>
            </>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  id={`${ID_PREFIX}-received-at`}
                  label="Received"
                  required
                  type="datetime-local"
                  description="When it arrived at the receiving desk. It cannot be in the future."
                  value={receivedAt}
                  onChange={(e) => setReceivedAt(e.target.value)}
                  className="font-mono tabular-nums"
                  disabled={saving}
                  error={error?.fieldError('receivedAt')}
                />

                <Select
                  id={`${ID_PREFIX}-receiving-org-unit-id`}
                  label="Receiving unit"
                  required
                  description={
                    unitLocked
                      ? 'It has already been sent on from this unit, so the unit that received it can no longer be changed.'
                      : 'The unit that took it in. It is with this unit until it is routed on.'
                  }
                  value={unitId}
                  onChange={setUnitId}
                  options={receivingOptions}
                  remote
                  loading={loadingUnits}
                  disabled={saving || unitLocked}
                  error={error?.fieldError('receivingOrgUnitId')}
                />
              </div>

              {/* FULL WIDTH, outside the grid — the remarks rule. */}
              <Textarea
                id={`${ID_PREFIX}-remarks`}
                label="Remarks"
                description="A free note about the document — how many copies came, what was enclosed, anything the next desk should know."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                disabled={saving}
                error={error?.fieldError('remarks')}
              />
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
